import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Divider,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckIcon from '@mui/icons-material/Check';

// Section components
import PersonalInfoSection from './PersonalInfoSection';
import SummarySection from './SummarySection';
import EducationSection from './EducationSection';
import ExperienceSection from './ExperienceSection';
import ProjectsSection from './ProjectsSection';
import PublicationsSection from './PublicationsSection';
import CertificationsSection from './CertificationsSection';
import AwardsSection from './AwardsSection';
import SkillsSection from './SkillsSection';
import CodingProfilesSection from './CodingProfilesSection';
import LinksSection from './LinksSection';

const ResumeTab = ({ userProfile, userId, authAxios, onUpdate, setError, onTriggerUpload, parsedDataToApply }) => {
    const [loading, setLoading] = useState(true);
    const fileInputRef = useRef(null);
    const [savingEntireProfile, setSavingEntireProfile] = useState(false);
    
    // Data state - loaded from APIs
    const [personalInfo, setPersonalInfo] = useState(null);
    const [summary, setSummary] = useState('');
    const [education, setEducation] = useState([]);
    const [experience, setExperience] = useState([]);
    const [projects, setProjects] = useState([]);
    const [publications, setPublications] = useState([]);
    const [certifications, setCertifications] = useState([]);
    const [awards, setAwards] = useState([]);
    const [skills, setSkills] = useState([]);
    const [codingProfiles, setCodingProfiles] = useState([]);
    const [links, setLinks] = useState([]);

    // Fetch data from APIs
    const fetchProfileData = useCallback(async () => {
        if (!authAxios) return;
        
        setLoading(true);
        try {
            const [
                publicationsRes,
                certificationsRes,
                awardsRes,
                codingProfilesRes,
                linksRes
            ] = await Promise.all([
                authAxios.get('/publications/').catch(() => ({ data: { results: [] } })),
                authAxios.get('/certifications/').catch(() => ({ data: { results: [] } })),
                authAxios.get('/awards/').catch(() => ({ data: { results: [] } })),
                authAxios.get('/coding-profiles/').catch(() => ({ data: { results: [] } })),
                authAxios.get('/links/').catch(() => ({ data: { results: [] } })),
            ]);

            setPublications(publicationsRes.data?.results || publicationsRes.data || []);
            setCertifications(certificationsRes.data?.results || certificationsRes.data || []);
            setAwards(awardsRes.data?.results || awardsRes.data || []);
            setCodingProfiles(codingProfilesRes.data?.results || codingProfilesRes.data || []);
            setLinks(linksRes.data?.results || linksRes.data || []);
        } catch (error) {
            console.error('Error fetching profile data:', error);
        } finally {
            setLoading(false);
        }
    }, [authAxios]);

    // Initialize from userProfile
    useEffect(() => {
        if (userProfile) {
            setPersonalInfo({
                first_name: userProfile.first_name || '',
                last_name: userProfile.last_name || '',
                mobile_number: userProfile.mobile_number || '',
                linkedin_url: userProfile.linkedin_url || '',
                date_of_birth: userProfile.date_of_birth || '',
                address_line_1: userProfile.address_line_1 || '',
                address_line_2: userProfile.address_line_2 || '',
                city: userProfile.city || '',
                state: userProfile.state || '',
                country: userProfile.country || '',
                postal_code: userProfile.postal_code || '',
                profile_picture_url: userProfile.profile_picture_url || '',
            });
            
            setSummary(userProfile.bio || '');
            setEducation(userProfile.education || []);
            setExperience(userProfile.experience || []);
            setProjects(userProfile.projects || []);
            
            // Sanitize skills
            const userSkills = userProfile.skills || [];
            const sanitizedSkills = Array.isArray(userSkills) 
                ? userSkills.map(skill => 
                    typeof skill === 'string' ? skill : (skill?.name || JSON.stringify(skill))
                  )
                : [];
            setSkills(sanitizedSkills);
            
            // Use data from userProfile if available
            if (userProfile.publications) setPublications(userProfile.publications);
            if (userProfile.certifications) setCertifications(userProfile.certifications);
            if (userProfile.awards) setAwards(userProfile.awards);
            if (userProfile.coding_profiles) setCodingProfiles(userProfile.coding_profiles);
            if (userProfile.links) setLinks(userProfile.links);
            
            // Check for toast notification
            const locationState = window.history.state?.usr;
            if (locationState?.showCompletenessToast) {
                setError?.("Please complete your profile to continue");
                window.history.replaceState({ ...window.history.state, usr: {} }, '');
            }
        }
    }, [userProfile, setError]);

    // Fetch API data on mount
    useEffect(() => {
        fetchProfileData();
    }, [fetchProfileData]);

    // Handle resume upload - triggers parent's upload flow with modal
    const handleResumeUpload = useCallback((event) => {
        if (onTriggerUpload) {
            onTriggerUpload(event);
        }
    }, [onTriggerUpload]);

    // Handle parsed data from parent
    useEffect(() => {
        if (parsedDataToApply) {
            if (parsedDataToApply.first_name || parsedDataToApply.last_name) {
                setPersonalInfo(prev => ({
                    ...prev,
                    first_name: parsedDataToApply.first_name || prev?.first_name || '',
                    last_name: parsedDataToApply.last_name || prev?.last_name || '',
                    mobile_number: parsedDataToApply.mobile_number || prev?.mobile_number || '',
                }));
            }
            if (parsedDataToApply.bio) setSummary(parsedDataToApply.bio);
            if (parsedDataToApply.education?.length) setEducation(parsedDataToApply.education);
            if (parsedDataToApply.experience?.length) setExperience(parsedDataToApply.experience);
            if (parsedDataToApply.projects?.length) setProjects(parsedDataToApply.projects);
            if (parsedDataToApply.skills?.length) setSkills(parsedDataToApply.skills);
        }
    }, [parsedDataToApply]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress sx={{ color: '#4f46e5' }} />
            </Box>
        );
    }

    // Save entire profile handler
    const saveEntireProfile = async () => {
        setSavingEntireProfile(true);
        try {
            // Trigger onUpdate to let parent know profile needs refresh
            onUpdate?.();
            setError?.('Profile saved successfully!');
        } catch (error) {
            console.error('Failed to save entire profile:', error);
            setError?.('Failed to save profile. Please try again.');
        } finally {
            setSavingEntireProfile(false);
        }
    };

    return (
        <Box sx={{ maxWidth: '900px', mx: 'auto', py: 4 }}>
            {/* Personal Info Section */}
            <PersonalInfoSection
                initialData={personalInfo}
                authAxios={authAxios}
                setError={setError}
                onUpdate={(data) => {
                    setPersonalInfo(data);
                    // Personal info updates the main user fields, so just trigger refetch
                    onUpdate?.();
                }}
            />

            <Divider sx={{ my: 4 }} />

            {/* Resume Upload Section - Mercor Style */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                    Resume <span style={{ color: '#4f46e5' }}>*</span>
                </Typography>
                <Box
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                        border: '1px dashed',
                        borderColor: userProfile?.resumes?.length > 0 ? '#4f46e5' : '#d1d5db',
                        borderRadius: '12px',
                        py: 8,
                        px: { xs: 2, md: 10 },
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            borderColor: '#4f46e5',
                        },
                    }}
                >
                    {userProfile?.resumes?.length > 0 ? (
                        <>
                            <Box sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                bgcolor: '#4f46e5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2,
                            }}>
                                <CheckIcon sx={{ color: 'white', fontSize: 28 }} />
                            </Box>
                            <Typography sx={{ fontWeight: 500, fontSize: '1rem' }}>
                                {userProfile.resumes[userProfile.resumes.length - 1]?.file_name || 'Resume uploaded'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                {userProfile.resumes.length} resume{userProfile.resumes.length > 1 ? 's' : ''} uploaded
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Box sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                bgcolor: '#4f46e5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2,
                            }}>
                                <CloudUploadIcon sx={{ color: 'white', fontSize: 28 }} />
                            </Box>
                            <Typography sx={{ fontWeight: 500, color: '#4f46e5', fontSize: '1rem' }}>
                                Upload your resume
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                PDF format, max 10MB
                            </Typography>
                        </>
                    )}
                </Box>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    style={{ display: 'none' }}
                    onChange={handleResumeUpload}
                />
            </Box>

            {/* Summary Section */}
            <SummarySection
                initialData={summary}
                authAxios={authAxios}
                setError={setError}
                onUpdate={(data) => {
                    setSummary(data);
                    onUpdate?.({ section: 'bio', data });
                }}
            />
            <Divider sx={{ my: 4 }} />

            {/* Education Section */}
            <EducationSection
                initialData={education}
                authAxios={authAxios}
                setError={setError}
                onUpdate={(data) => {
                    setEducation(data);
                    onUpdate?.({ section: 'education', data });
                }}
            />
            <Divider sx={{ my: 4 }} />

            {/* Experience Section */}
            <ExperienceSection
                initialData={experience}
                authAxios={authAxios}
                setError={setError}
                onUpdate={(data) => {
                    setExperience(data);
                    onUpdate?.({ section: 'experience', data });
                }}
            />
            <Divider sx={{ my: 4 }} />

            {/* Projects Section */}
            <ProjectsSection
                initialData={projects}
                authAxios={authAxios}
                setError={setError}
                onUpdate={(data) => {
                    setProjects(data);
                    onUpdate?.({ section: 'projects', data });
                }}
            />
            <Divider sx={{ my: 4 }} />

            {/* Publications Section */}
            <PublicationsSection
                initialData={publications}
                authAxios={authAxios}
                setError={setError}
                onUpdate={(data) => {
                    setPublications(data);
                    onUpdate?.({ section: 'publications', data });
                }}
            />
            <Divider sx={{ my: 4 }} />

            {/* Certifications Section */}
            <CertificationsSection
                initialData={certifications}
                authAxios={authAxios}
                setError={setError}
                onUpdate={(data) => {
                    setCertifications(data);
                    onUpdate?.({ section: 'certifications', data });
                }}
            />
            <Divider sx={{ my: 4 }} />

            {/* Awards Section */}
            <AwardsSection
                initialData={awards}
                authAxios={authAxios}
                setError={setError}
                onUpdate={(data) => {
                    setAwards(data);
                    onUpdate?.({ section: 'awards', data });
                }}
            />
            <Divider sx={{ my: 4 }} />

            {/* Skills Section */}
            <SkillsSection
                initialData={skills}
                authAxios={authAxios}
                setError={setError}
                onUpdate={(data) => {
                    setSkills(data);
                    onUpdate?.({ section: 'skills', data });
                }}
            />
            <Divider sx={{ my: 4 }} />

            {/* Coding Profiles Section */}
            <CodingProfilesSection
                initialData={codingProfiles}
                authAxios={authAxios}
                setError={setError}
                onUpdate={(data) => {
                    setCodingProfiles(data);
                    onUpdate?.({ section: 'coding_profiles', data });
                }}
            />
            <Divider sx={{ my: 4 }} />

            {/* Links Section */}
            <LinksSection
                initialData={links}
                authAxios={authAxios}
                setError={setError}
                onUpdate={(data) => {
                    setLinks(data);
                    onUpdate?.({ section: 'links', data });
                }}
            />

            {/* Sticky Save Profile Button */}
            <Box
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    py: 3,
                    px: 4,
                    mt: 6,
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(8px)',
                    borderTop: '1px solid #e5e7eb',
                    boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 2,
                    zIndex: 100,
                    borderRadius: '12px 12px 0 0',
                }}
            >
                <Button
                    variant="contained"
                    onClick={saveEntireProfile}
                    disabled={savingEntireProfile}
                    sx={{
                        bgcolor: '#4f46e5',
                        textTransform: 'none',
                        px: 6,
                        py: 1.5,
                        borderRadius: '8px',
                        fontWeight: 700,
                        fontSize: '1rem',
                        minWidth: 200,
                        boxShadow: '0 4px 12px rgba(79, 70, 229, 0.4)',
                        '&:hover': { 
                            bgcolor: '#4338ca',
                            boxShadow: '0 6px 16px rgba(79, 70, 229, 0.5)',
                        },
                        '&:disabled': {
                            opacity: 0.7,
                        },
                    }}
                >
                    {savingEntireProfile ? (
                        <>
                            <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                            Saving...
                        </>
                    ) : (
                        'Save Profile'
                    )}
                </Button>
            </Box>
        </Box>
    );
};

export default ResumeTab;
